/**
 * Claude analysis panel — surfaces the structured decision (path, signal,
 * reasoning) plus the raw JSON Claude returned. The raw JSON is hidden behind
 * a `<details>` toggle to keep the panel scannable.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Brain } from 'lucide-react'

interface Props {
  readonly classificationPattern: string | null
  readonly qualificationResult: string | null
  readonly reasoning: string | null
  readonly rawClaudeResponse: string | null
}

export function ClaudeAnalysisPanel({
  classificationPattern,
  qualificationResult,
  reasoning,
  rawClaudeResponse,
}: Props) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Brain className="h-4 w-4" />
          Claude analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Decision</dt>
          <dd className="font-medium">{qualificationResult ?? '—'}</dd>
          <dt className="text-muted-foreground">Pattern</dt>
          <dd className="font-medium">{classificationPattern ?? '—'}</dd>
        </dl>

        {reasoning ? (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Reasoning
            </p>
            <p className="text-sm whitespace-pre-wrap">{reasoning}</p>
          </div>
        ) : null}

        {rawClaudeResponse ? (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs uppercase tracking-wide text-muted-foreground">
              Raw response (JSON)
            </summary>
            <pre className="mt-2 overflow-x-auto rounded-md bg-muted/50 p-3 text-[11px]">
              {rawClaudeResponse}
            </pre>
          </details>
        ) : null}
      </CardContent>
    </Card>
  )
}
